//
import type { AxiosCollection } from '@/types/Components/Response'
import type { AssignmentCreationData, AssignmentPiece, ErrorsList } from '@/types/Models/Assignment'
import type { PickupDate } from '@/types/Models/Pickup'
import type { AssignmentFormStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { toRaw } from 'vue'
import { defaultPiece } from '@/composables/Pieces/defaultPiece'
import { PRIORITY } from '@/const/AssignmentServiceLevels'
import { CONSOLIDATE, SINGLE } from '@/const/AssignmentTypes'
import { PACKAGE } from '@/const/DistributionChannels'
import { DEFAULT_LOADER } from '@/const/LoadersNames'
import { CREATE_ASSIGNMENT_SOURCE_FILE_ERRORS, REQUEST_ERROR } from '@/const/ModalTypes'
import { OTHER } from '@/const/ProformaInvoiceTypes'
import { catchErrors, none } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useAccountStore } from '@/store/account'
import { useAddressBookStore } from '@/store/addressBook'
import { useCustomersStore } from '@/store/customers'
import { useUiStore } from '@/store/ui'
import PickupProxy from '@/proxies/PickupProxy'
import AssignmentProxy from '@/proxies/AssignmentProxy'
import AssignmentTransformer from '@/transformers/AssignmentTransformer'

const defaultErrors: ErrorsList = {
  company_id: '',
  file: '',
  sender_address_id: '',
  pickup_date: false,
}

const createDefaultAssignment = (): AssignmentCreationData => ({
  pickup_date: '',
  pickup_instructions: '',
  file: null,
  company_id: '',
  sender_address_id: '',
  service_level: PRIORITY,
  description: '',
  message: '',
  customer_reference: '',
  distribution_channel: PACKAGE,
  assignment_type: CONSOLIDATE,
  item: {
    is_b2b_shipment: false,
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    country_code: '',
    city: '',
    postal_code: '',
    address: '',
    secondary_address: '',
    delivery_type: '',
    weight: '',
    height: '',
    length: '',
    width: '',
    reference: '',
    estimated_cost: '',
    invoiceType: OTHER,
    currency: 'SEK',
    pieces: [{ ...defaultPiece }],
  },
})

const createDefaultState = (): AssignmentFormStoreState => ({
  assignment: createDefaultAssignment(),
  errors: { ...defaultErrors },
  pickupDates: [],
  saveToAddressBook: false,
  addressBookEntryId: null,
})

export const useAssignmentFormStore = defineStore('assignmentForm', {
  state: (): AssignmentFormStoreState => createDefaultState(),
  actions: {
    reset() {
      this.assignment = createDefaultAssignment()
      this.saveToAddressBook = false
      this.addressBookEntryId = null
    },
    setSaveToAddressBook(value: boolean) {
      this.saveToAddressBook = value
    },
    setAddressBookEntry(id: string | null) {
      this.addressBookEntryId = id
    },
    setFields(data: Partial<AssignmentCreationData>) {
      if (data.assignment_type && data.assignment_type !== SINGLE) {
        this.saveToAddressBook = false
        this.addressBookEntryId = null
      }

      if (data.company_id !== undefined && data.company_id !== this.assignment.company_id) {
        this.saveToAddressBook = false
        this.addressBookEntryId = null
      }

      const normalizedData = { ...data }

      if (normalizedData.item && Object.prototype.hasOwnProperty.call(normalizedData.item, 'company_name')) {
        normalizedData.item.is_b2b_shipment = normalizedData.item.company_name?.trim()?.length > 2
      }

      // Deep merge for item to preserve pieces
      if (normalizedData.item) {
        const currentPieces = [...this.assignment.item.pieces]
        const incomingPieces = normalizedData.item.pieces
        const shouldUseIncomingPieces = Array.isArray(incomingPieces) && incomingPieces.length > 0
          && incomingPieces.some((p: AssignmentPiece) => p.description || p.weight || p.commodity_code)

        normalizedData.item = {
          ...toRaw(this.assignment.item),
          ...normalizedData.item,
          pieces: shouldUseIncomingPieces ? incomingPieces : currentPieces,
        }
      }

      this.assignment = { ...toRaw(this.assignment), ...normalizedData }
    },
    updatePieces({ index, piece }: { index: number; piece?: AssignmentPiece }) {
      const pieces = [...this.assignment.item.pieces]

      if (piece) {
        if (index >= pieces.length) {
          // Adding new piece
          pieces.push(piece)
        } else {
          // Updating existing piece
          pieces.splice(index, 1, piece)
        }
      } else {
        pieces.splice(index, 1)
      }

      this.assignment.item.pieces = pieces.length > 0 ? pieces : [{ ...defaultPiece }]
    },
    resetPieces() {
      this.assignment.item.pieces = [{ ...defaultPiece }]
    },
    resetErrors() {
      this.errors = { ...defaultErrors }
    },
    setErrors(errors: Partial<ErrorsList>) {
      this.errors = { ...toRaw(this.errors), ...errors }
    },
    submit(isStaff: boolean) {
      this.resetErrors()
      const uiStore = useUiStore()
      uiStore.loaderShow({ name: DEFAULT_LOADER })

      const options =
        this.assignment.assignment_type === CONSOLIDATE ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}

      return new AssignmentProxy()
        .create(AssignmentTransformer.create(this.assignment, isStaff), options)
        .then((raw: any) => {
          const accountStore = useAccountStore()
          const customersStore = useCustomersStore()
          const companyId = this.assignment.company_id
          const isAddressBookAvailable = accountStore.isStaff
            ? customersStore.allCustomers.find((c) => c.id === companyId)?.isAddressBookAvailable ?? false
            : customersStore.customer?.isAddressBookAvailable ?? false

          if (this.saveToAddressBook && this.assignment.assignment_type === SINGLE && isAddressBookAvailable) {
            const item = this.assignment.item
            const addressBookStore = useAddressBookStore()
            const addressData = {
              type: 'recipient',
              company_id: this.assignment.company_id || undefined,
              first_name: item.first_name || null,
              last_name: item.last_name || null,
              company: item.company_name || null,
              country_code: item.country_code,
              postal_code: item.postal_code || null,
              city: item.city || null,
              address_line: item.address || null,
              address_line2: item.secondary_address || null,
              phone_number: item.phone_number || null,
              email: item.email || null,
            }
            if (this.addressBookEntryId) {
              addressBookStore.update(this.addressBookEntryId, addressData).catch(none)
            } else {
              addressBookStore.createSingle(addressData).catch(none)
            }
          }
          return raw
        })
        .catch((error: any) => {
          const fileErrors: string[] = error?.errors?.file || []
          if (fileErrors.length > 0) {
            openModal(CREATE_ASSIGNMENT_SOURCE_FILE_ERRORS, { fileErrors })
          } else if (error?.errors) {
            openModal(REQUEST_ERROR, { errors: error.errors })
          } else {
            catchErrors(error)
          }
        })
        .finally(() => {
          uiStore.loaderHide({ name: DEFAULT_LOADER })
        })
    },
    fetchDates(countryCode?: string) {
      return new PickupProxy()
        .getPickupDates(countryCode)
        .then((response: AxiosCollection<PickupDate>) => {
          this.pickupDates = response.data
          return response
        })
        .catch(catchErrors)
    },
  },
})

export type AssignmentFormStore = ReturnType<typeof useAssignmentFormStore>
